import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import VipCreditIcon from '~/icons/components/VipCreditIcon';
import { FormattedMessage, useCurrency } from '~/techstyle-shared/react-intl';

const DynamicFormattedMessage = FormattedMessage;

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background-color: ${({ theme }) => theme.colors.backgroundEmphasized};
  padding: 0 0 0 10px;
`;

const IconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.backgroundEmphasized};
`;

const TextBlock = styled.div`
  border-left: 1px solid ${({ theme }) => theme.colors.textLight};
  padding: 10px 16px;
`;

const Line = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
`;

const HighlightEmphasis = styled.strong`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.outfitSavingsEmphasis};
`;

const BoldEmphasis = styled.strong`
  font-weight: 700;
`;

export default function OutfitSavingsBanner({
  outfitValue,
  creditCount,
  savingsAmount,
}) {
  const { formatCurrency } = useCurrency();

  const formattedOutfitValue = formatCurrency(outfitValue, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const formattedSavings = formatCurrency(savingsAmount, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const creditsLabel =
    creditCount === 1
      ? `${creditCount} VIP Credit`
      : `${creditCount} VIP Credits`;

  return (
    <Banner>
      <IconCircle>
        <VipCreditIcon size="24px" color="white" />
      </IconCircle>
      <TextBlock>
        <Line>
          <DynamicFormattedMessage
            id="site_pdp.outfit_savings_banner"
            defaultMessage="Get this outfit valued at {outfitValue}, for only {credits}! You'll save {savingsAmount} by using your credit."
            values={{
              outfitValue: (
                <HighlightEmphasis>{formattedOutfitValue}</HighlightEmphasis>
              ),
              credits: <HighlightEmphasis>{creditsLabel}</HighlightEmphasis>,
              savingsAmount: <BoldEmphasis>{formattedSavings}</BoldEmphasis>,
            }}
          />
        </Line>
      </TextBlock>
    </Banner>
  );
}

OutfitSavingsBanner.propTypes = {
  /** Number of VIP credits needed to redeem the outfit */
  creditCount: PropTypes.number.isRequired,
  /** Full retail value of the outfit */
  outfitValue: PropTypes.number.isRequired,
  /** Dollar amount the member saves vs. paying retail */
  savingsAmount: PropTypes.number.isRequired,
};
